import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "reactstrap";
import {
  ChevronRight,
  Download,
  MoreVertical,
  Package,
  Pencil,
  Plus,
  Search as SearchIcon,
  Sparkles,
  Trash2,
} from "lucide-react";
import * as XLSX from "xlsx";

import { deleteOneBrand, getAllBrands } from "../../../../redux/brand/action";
import axiosInstance from "../../../../redux/api";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import Pagination from "../../../Common/Pagination/CommonPagination";
import DeleteBrand from "./DeleteBrand";
import "../../../Common/ListShell/ListShell.css";

const formatDateTime = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return { date: datePart, time: timePart };
};

const RowActions = ({ canDelete, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="nl-rowactions">
      <button
        type="button"
        className="nl-iconbtn"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        aria-label="Edit"
        title="Edit"
      >
        <Pencil size={14} aria-hidden="true" />
      </button>
      {canDelete && (
        <button
          type="button"
          className="nl-iconbtn nl-iconbtn--danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete"
          title="Delete"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      )}
      <div className="nl-menu" ref={ref}>
        <button
          type="button"
          className="nl-iconbtn"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          aria-label="More options"
        >
          <MoreVertical size={14} aria-hidden="true" />
        </button>
        {open && (
          <div className="nl-menu__panel" role="menu" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="nl-menu__item"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              Edit
            </button>
            {canDelete && (
              <button
                type="button"
                className="nl-menu__item nl-menu__item--danger"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Three-zone row grid: brand cell · status · created · actions
const ROW_GRID = "minmax(0, 1.6fr) 130px 180px 110px";

const BrandList = () => {
  const { hasAction } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [params, setParams] = useState({
    page: 1,
    search_key: "",
    status: "",
  });
  const [searchtext, setSearchtext] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal] = useState(false);
  const [currentComponent, setCurrentComponent] = useState({});

  const brandList = useSelector(
    (state) => state.reusableSlice.brandList || [],
  );

  const canDelete = hasAction(MENU.CATALOGUE, PERMISSION.DELETE);

  useEffect(() => {
    dispatch(getAllBrands(params));
  }, [params, dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setParams((prev) => ({ ...prev, search_key: searchtext, page: 1 }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchtext]);

  const filteredItems = useMemo(() => {
    if (!params.status) return brandList;
    return brandList.filter(
      (item) => item.status === parseInt(params.status, 10),
    );
  }, [brandList, params.status]);

  const counts = useMemo(() => {
    const total = brandList.length;
    const active = brandList.filter((b) => b.status === 1).length;
    const inactive = total - active;
    const hidden = brandList.filter(
      (b) => b.home_view === false || b.home_view === "false",
    ).length;
    return { total, active, inactive, hidden };
  }, [brandList]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const currentPageItems = filteredItems.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const deleteComponent = () => {
    if (currentComponent?.id) {
      dispatch(deleteOneBrand(currentComponent.id));
    }
  };

  const setStatusFilter = (next) => {
    setParams((prev) => ({ ...prev, status: next, page: 1 }));
    setCurrentPage(1);
  };

  const handleExport = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post("brand/get_all", {
        ...params,
        page: "",
      });
      const list = response?.data?.response || [];
      if (!list.length) return;

      const header = [["Sl", "Name", "Products", "Status", "Created On", "Updated By"]];
      const data = list.map((item, i) => [
        i + 1,
        item.name,
        Number.isFinite(Number(item.product_count)) ? Number(item.product_count) : "",
        item.status === 1 ? "Active" : item.status === 2 ? "Inactive" : "",
        item.created_on
          ? new Date(item.created_on).toLocaleString("en-US", { timeZone: "UTC" })
          : "",
        item.updated_by_name || "",
      ]);
      const ws = XLSX.utils.aoa_to_sheet(header);
      XLSX.utils.sheet_add_aoa(ws, data, { origin: "A2" });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Brand List");
      XLSX.writeFile(wb, "BrandList.xlsx");
    } catch (err) {
      console.error("Brand export error:", err);
    }
  };

  return (
    <div className="nl-page">
      {/* Crumb */}
      <div className="nl-topbar">
        <span className="nl-topbar__crumb">Catalog</span>
        <ChevronRight size={12} className="nl-topbar__sep" aria-hidden="true" />
        <span className="nl-topbar__current">Brands</span>
        <span className="nl-topbar__count">{counts.total.toLocaleString()}</span>
      </div>

      {/* Header band */}
      <header className="nl-header">
        <div className="nl-header__row">
          <div className="nl-header__title-block">
            <span className="nl-header__eyebrow">
              <Sparkles size={11} aria-hidden="true" />
              Catalog
            </span>
            <h1 className="nl-header__title">Brands</h1>
            <p className="nl-header__sub">
              Manage the brands you stock and how they appear across the storefront.
            </p>
          </div>
          <div className="nl-header__actions">
            <button
              type="button"
              className="nb-btn nb-btn--ghost"
              onClick={handleExport}
            >
              <Download size={14} aria-hidden="true" />
              Export CSV
            </button>
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => navigate("/addbrand")}
            >
              <Plus size={14} aria-hidden="true" />
              New brand
            </button>
          </div>
        </div>

        <div className="nl-stats" role="group" aria-label="Brand stats">
          <span className="nl-stat nl-stat--total">
            <strong>{counts.total.toLocaleString()}</strong>
            <em>total</em>
          </span>
          <span className="nl-stat nl-stat--active">
            <strong>{counts.active.toLocaleString()}</strong>
            <em>active</em>
          </span>
          <span className="nl-stat nl-stat--inactive">
            <strong>{counts.inactive.toLocaleString()}</strong>
            <em>inactive</em>
          </span>
          {counts.hidden > 0 && (
            <span className="nl-stat nl-stat--neutral">
              <strong>{counts.hidden.toLocaleString()}</strong>
              <em>hidden from home</em>
            </span>
          )}
        </div>
      </header>

      {/* Toolbar */}
      <div className="nl-toolbar">
        <div className="nl-search">
          <SearchIcon size={14} className="nl-search__icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search brands…"
            value={searchtext}
            onChange={(e) => setSearchtext(e.target.value)}
          />
          {searchtext && (
            <button
              type="button"
              className="nl-search__clear"
              onClick={() => setSearchtext("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="nl-chips">
          <span className="nl-chiplabel">Status</span>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--neutral" />
            All
          </button>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "1" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("1")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--on" />
            Active
          </button>
          <button
            type="button"
            className={`nl-chipfilter ${params.status === "2" ? "is-active" : ""}`}
            onClick={() => setStatusFilter("2")}
          >
            <span className="nl-chipfilter__dot nl-chipfilter__dot--off" />
            Inactive
          </button>
        </div>

        <div className="nl-toolbar__spacer" />
      </div>

      {/* Table */}
      <div className="nl-tablewrap">
        <div className="nl-thead" style={{ gridTemplateColumns: ROW_GRID }}>
          <span>Brand</span>
          <span>Status</span>
          <span>Created</span>
          <span></span>
        </div>

        {currentPageItems.length > 0 ? (
          <div className="nl-tbody">
            {currentPageItems.map((item) => {
              const isActive = item.status === 1;
              const dt = formatDateTime(item.created_on || item.updated_on);
              return (
                <div
                  key={item.id}
                  className="nl-row"
                  style={{ gridTemplateColumns: ROW_GRID }}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/upadtebrand/${item.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/upadtebrand/${item.id}`);
                  }}
                >
                  <div className="nl-cell-brand">
                    <div className={`nl-avatar ${item.image ? "" : "is-empty"}`}>
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <span>{(item.name || "?").charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="nl-cell-meta">
                      <div className="nl-cell-meta__title">
                        <strong>{item.name}</strong>
                        {Number.isFinite(Number(item.product_count)) && (
                          <span
                            className="nl-count"
                            title={`${item.product_count} ${Number(item.product_count) === 1 ? "product" : "products"} linked`}
                          >
                            ({Number(item.product_count).toLocaleString()})
                          </span>
                        )}
                      </div>
                      {item.ar_name && (
                        <div className="nl-cell-meta__sub">{item.ar_name}</div>
                      )}
                    </div>
                  </div>

                  <div className="nl-cell">
                    <span
                      className={`nl-badge ${
                        isActive ? "nl-badge--on" : "nl-badge--off"
                      }`}
                    >
                      <span className="nl-badge__dot" aria-hidden="true" />
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="nl-cell nl-cell--date">
                    {typeof dt === "object" ? (
                      <>
                        <span>{dt.date}</span>
                        <span>{item.updated_by_name ? `by ${item.updated_by_name}` : dt.time}</span>
                      </>
                    ) : (
                      <span>{dt}</span>
                    )}
                  </div>

                  <RowActions
                    canDelete={canDelete}
                    onEdit={() => navigate(`/upadtebrand/${item.id}`)}
                    onDelete={() => {
                      setModal(true);
                      setCurrentComponent(item);
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="nl-empty">
            <span className="nl-empty__art">
              <Package size={26} aria-hidden="true" />
            </span>
            <h3 className="nl-empty__title">No brands yet</h3>
            <p className="nl-empty__sub">
              {searchtext
                ? `No brands match "${searchtext}"`
                : "Create your first brand to associate with products."}
            </p>
            {!searchtext && (
              <button
                type="button"
                className="nb-btn nb-btn--primary"
                onClick={() => navigate("/addbrand")}
              >
                <Plus size={14} aria-hidden="true" />
                New brand
              </button>
            )}
          </div>
        )}
      </div>

      {currentPageItems.length > 0 && (
        <div className="nl-paginate">
          <div className="nl-perpage">
            <span>Show</span>
            <select
              className="nl-perpage__select"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>per page</span>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal((s) => !s)}>
        <DeleteBrand
          setModal={setModal}
          currentComponent={currentComponent}
          deleteComponent={deleteComponent}
        />
      </Modal>
    </div>
  );
};

export default BrandList;
